import React, { useEffect, useState } from "react";
import { Sparkles, UserPlus, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { formatDate, downloadCsv } from "./adminHelpers";

export default function PreviewLeadsPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [sendEmail, setSendEmail] = useState(false);
  const load = () => api.get("/admin/preview-leads").then(({ data }) => setRows(data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const convert = async (lead) => {
    setBusy(lead.id);
    try {
      const { data } = await api.post(`/admin/preview-leads/${lead.id}/convert`, null,
        { params: { send_welcome_email: sendEmail } });
      const base = data.already ? `Already an Enterprise360 client: ${data.client_name}` : `Created Enterprise360 client: ${data.client_name}`;
      toast.success(sendEmail && data.welcome_email_sent ? `${base} · welcome email sent` : base);
      load();
    } catch {
      toast.error("Could not create the client");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div data-testid="admin-preview-leads-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="ek-heading text-xl flex items-center gap-2"><Sparkles size={16} className="text-[var(--e360-primary,#7737F6)]" /> Preview leads</h2>
          <p className="text-[12px] ek-text-muted mt-1 max-w-2xl">High-intent prospects who generated a live Enterprise360 blueprint preview for their own sector on <code className="text-[12px]">/system-of-proof-in-action</code> and asked us to follow up.</p>
        </div>
        <button type="button" onClick={() => downloadCsv("/admin/preview-leads.csv", "ekselens-preview-leads.csv")} className="ek-btn ek-btn-outline text-[12px]" data-testid="export-preview-leads-csv">Export CSV</button>
      </div>
      <label className="flex items-center gap-2 text-[12px] ek-text-soft mb-4" data-testid="preview-lead-welcome-toggle">
        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="preview-lead-welcome-checkbox" />
        Send an optional "engagement starting" welcome email to the lead when creating the client
      </label>
      <div className="overflow-x-auto border ek-line">
        <table className="min-w-full text-[13px]">
          <thead className="ek-surface-2 ek-text-muted text-left">
            <tr>
              <th className="px-3 py-2">Date</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Organisation</th><th className="px-3 py-2">Sector</th><th className="px-3 py-2">Resolved archetype</th><th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center ek-text-muted text-[13px]">
                <div className="font-medium ek-text-soft">No preview leads yet.</div>
                <div className="mt-1 ek-text-muted">When a visitor uses “Generate my organisation’s version” on the public showcase and submits their details, they land here.</div>
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t ek-line" data-testid={`preview-lead-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.created_at)}</td>
                <td className="px-3 py-2 font-medium">{r.full_name}</td>
                <td className="px-3 py-2">{r.email}</td>
                <td className="px-3 py-2">{r.organisation || "—"}</td>
                <td className="px-3 py-2 capitalize">{String(r.sector || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2">{r.archetype || "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.converted_client_id ? (
                    <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700 font-medium" data-testid={`preview-lead-converted-${r.id}`}>
                      <CheckCircle2 size={13} /> Client created
                    </span>
                  ) : (
                    <button type="button" onClick={() => convert(r)} disabled={busy === r.id}
                      data-testid={`preview-lead-convert-${r.id}`}
                      className="inline-flex items-center gap-1 ek-btn ek-btn-outline text-[12px] disabled:opacity-60">
                      <UserPlus size={13} /> {busy === r.id ? "Creating…" : "Create client"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
